import { useMemo, useState } from "react";

import { Download, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ToastProvider";
import { useCapabilities } from "@/hooks/useCapabilities";

type MCExportButtonProps = {
  label: string;
  endpoint: string;
  filename: string;
  capability?: string;
};

function buildFilename(filename: string): string {
  const timestamp = new Date().toISOString().slice(0, 10);
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1) {
    return `${filename}_${timestamp}`;
  }
  const base = filename.slice(0, lastDot);
  const ext = filename.slice(lastDot + 1);
  return `${base}_${timestamp}.${ext}`;
}

export function MCExportButton({ label, endpoint, filename, capability }: MCExportButtonProps) {
  const [loading, setLoading] = useState(false);
  const { push } = useToast();
  const { capabilities, hasFullAccess, ready } = useCapabilities();
  const allowed = useMemo(() => {
    if (!capability) return true;
    if (hasFullAccess) return true;
    if (!ready) return false;
    return Boolean((capabilities as Record<string, unknown> | undefined)?.[capability]);
  }, [capabilities, capability, hasFullAccess, ready]);

  const handleExport = async () => {
    if (!allowed) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(endpoint, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildFilename(filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      push({ title: "Export ready", message: `${label} downloaded.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to export file.";
      push({ title: "Export failed", message });
    } finally {
      setLoading(false);
    }
  };

  const button = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="gap-2"
      disabled={loading || !allowed}
      onClick={() => void handleExport()}
    >
      {allowed ? <Download className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
      {loading ? "Exporting…" : label}
    </Button>
  );

  if (allowed) {
    return button;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-sm leading-tight">
          Upgrade to unlock this export.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default MCExportButton;
